import { ChevronDown, ChevronRight, Loader2, Package, Plus, Search, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";

import {
  useCaricoPrevisto,
  useCpAggiungiCommessa,
  useCpCommesseCandidate,
  useCpComposizione,
  useCpCorreggiAttribuzione,
  useCpPatch,
  useCpScelteComposizione,
  useCpTogliCommessa,
} from "@/hooks/useM3";
import { cn } from "@/lib/utils";
import type { CommessaCandidata, CommessaComposizione, RigaComposizione } from "@/types/api";

/**
 * La sezione «Carico» del drawer: cosa c'è sul camion, commessa per commessa.
 *
 * Prima era una tabella in sola lettura di *tutte* le righe delle commesse
 * agganciate. Ma un camion non prende sempre una commessa intera — il foglio di
 * Marina scrive «805+ STRALCI 774» e «804 (3 PLT)» — e quella distinzione
 * finiva in una nota mentre il portale continuava a dire che c'era tutto.
 *
 * Il verso del gesto è quello in cui lavora Marina: **si prende la commessa
 * intera e si toglie ciò che non parte**, non si compone il camion riga per riga.
 * Da qui la selezione in blocco (una commessa = un clic) e le due esclusioni:
 * riga fuori, oppure quantità ridotta.
 *
 * Ogni riga porta la **disponibilità viva** dal motore di evadibilità M1 — a
 * magazzino adesso / in produzione / manca — con le stesse parole di «Dov'è la
 * merce» in M1, così le due schermate non raccontano la stessa cosa in due modi.
 *
 * ⚠️ Quando la composizione viene dalla NDP è in **sola lettura**: quella dice
 * cosa è uscito davvero, e la NDP vince sempre (deciso con Enrico).
 */
export function ComposizioneCarico({
  caricoId,
  gestioneCommesse = true,
}: {
  caricoId: string;
  /** `false` nel drawer della riga: aggiungere e togliere commesse si fa dalla
   *  card Commessa, qui restano le righe (30-09-2026). */
  gestioneCommesse?: boolean;
}) {
  const { data, isLoading } = useCpComposizione(caricoId);
  const [aperte, setAperte] = useState<Set<number>>(new Set());
  const [cercaCommessa, setCercaCommessa] = useState(false);

  if (isLoading) {
    return <p className="text-xs text-muted-foreground">Carico in lettura…</p>;
  }
  if (!data) {
    return <p className="text-xs text-muted-foreground">Composizione non disponibile.</p>;
  }

  const daNdp = !data.modificabile;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span
          className={cn(
            "inline-block rounded px-1.5 py-0.5 text-[11px]",
            daNdp ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700",
          )}
        >
          {daNdp ? "da NDP · non modificabile" : "ipotizzato · pre-NDP"}
        </span>
        {!daNdp && gestioneCommesse && (
          <button
            type="button"
            onClick={() => setCercaCommessa((v) => !v)}
            className="inline-flex items-center gap-1 rounded border px-2 py-1 text-[11px] font-medium hover:bg-muted"
          >
            <Plus className="h-3 w-3" />
            Aggiungi commessa
          </button>
        )}
      </div>

      {cercaCommessa && !daNdp && (
        <SelettoreCommessa caricoId={caricoId} onFatto={() => setCercaCommessa(false)} />
      )}

      <Attribuzione caricoId={caricoId} />

      {data.commesse.length === 0 && data.commesse_senza_righe.length === 0 && (
        <p className="text-xs text-muted-foreground">
          Nessuna commessa sul carico. Aggiungine una per vedere cosa c'è e cosa manca.
        </p>
      )}

      {data.commesse.map((c) => (
        <BloccoCommessa
          key={c.ordine_numero ?? "senza"}
          caricoId={caricoId}
          commessa={c}
          modificabile={!daNdp}
          togliibile={gestioneCommesse}
          aperta={c.ordine_numero != null && aperte.has(c.ordine_numero)}
          onToggle={() =>
            setAperte((prec) => {
              const n = new Set(prec);
              if (c.ordine_numero == null) return n;
              if (n.has(c.ordine_numero)) n.delete(c.ordine_numero);
              else n.add(c.ordine_numero);
              return n;
            })
          }
        />
      ))}

      {/* Agganciate ma non nell'export M1: non sono sparite, non le conosciamo
          riga per riga — 63 commesse su 71 del foglio erano in questo stato. */}
      {data.commesse_senza_righe.length > 0 && (
        <p className="rounded border border-dashed px-2 py-1.5 text-[11px] text-muted-foreground">
          Agganciate ma non nell'export ordini caricato:{" "}
          <span className="font-semibold tabular-nums">
            {data.commesse_senza_righe.join(", ")}
          </span>
          . Le righe si vedranno al prossimo caricamento dei dati.
        </p>
      )}

      {/* I pallet dalle righe sono una PROPOSTA: `pallet_previsti` può essere un
          valore di Marina, e nessuno sovrascrive Marina. */}
      {data.pallet_dalle_righe != null &&
        data.pallet_dalle_righe !== data.pallet_previsti && (
          <ProponiPallet
            caricoId={caricoId}
            calcolati={data.pallet_dalle_righe}
            attuali={data.pallet_previsti}
          />
        )}
    </div>
  );
}

function ProponiPallet({
  caricoId,
  calcolati,
  attuali,
}: {
  caricoId: string;
  calcolati: number;
  attuali: number | null;
}) {
  const patch = useCpPatch(caricoId);
  return (
    <div className="flex items-center justify-between gap-2 rounded border bg-muted/30 px-2 py-1.5 text-[11px]">
      <span>
        Pallet dalle righe incluse: <b className="tabular-nums">{calcolati}</b>
        {attuali != null && <> · in tabella: <b className="tabular-nums">{attuali}</b></>}
      </span>
      <button
        type="button"
        disabled={patch.isPending}
        onClick={() => patch.mutate({ pallet: calcolati })}
        className="rounded border px-2 py-0.5 font-medium hover:bg-card disabled:opacity-50"
      >
        Aggiorna
      </button>
    </div>
  );
}

function BloccoCommessa({
  caricoId,
  commessa,
  modificabile,
  togliibile = true,
  aperta,
  onToggle,
}: {
  caricoId: string;
  commessa: CommessaComposizione;
  modificabile: boolean;
  togliibile?: boolean;
  aperta: boolean;
  onToggle: () => void;
}) {
  const scelte = useCpScelteComposizione(caricoId);
  const togli = useCpTogliCommessa(caricoId);
  const conId = commessa.righe.filter((r) => r.riga_id);

  // Il gesto in blocco: tutte o nessuna, in una chiamata.
  function tutte(incluse: boolean) {
    scelte.mutate(conId.map((r) => ({ riga_id: r.riga_id as string, escluso: !incluse })));
  }

  return (
    <div className="rounded-md border">
      <div className="flex items-center gap-2 px-2 py-1.5">
        <button
          type="button"
          onClick={onToggle}
          className="flex min-w-0 flex-1 items-center gap-1.5 text-left text-xs font-semibold"
        >
          {aperta ? (
            <ChevronDown className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 shrink-0" />
          )}
          <span className="tabular-nums">{commessa.ordine_numero ?? "senza commessa"}</span>
          <span className="font-normal text-muted-foreground">
            {commessa.incluse}/{commessa.totali} righe
          </span>
          {commessa.parziale && (
            <span
              className="rounded bg-amber-100 px-1 py-0.5 text-[9.5px] font-medium text-amber-800"
              title="Righe escluse o quantità ridotte: il carico prende questa commessa solo in parte"
            >
              stralcio
            </span>
          )}
        </button>
        {modificabile && conId.length > 0 && (
          <>
            <button
              type="button"
              onClick={() => tutte(true)}
              disabled={scelte.isPending}
              className="rounded border px-1.5 py-0.5 text-[10.5px] hover:bg-muted disabled:opacity-50"
            >
              tutte
            </button>
            <button
              type="button"
              onClick={() => tutte(false)}
              disabled={scelte.isPending}
              className="rounded border px-1.5 py-0.5 text-[10.5px] hover:bg-muted disabled:opacity-50"
            >
              nessuna
            </button>
          </>
        )}
        {modificabile && togliibile && commessa.ordine_numero != null && (
          <button
            type="button"
            onClick={() => togli.mutate(commessa.ordine_numero as number)}
            disabled={togli.isPending}
            title="Togli la commessa dal carico"
            className="text-muted-foreground hover:text-red-600 disabled:opacity-50"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {aperta && (
        <ul className="border-t">
          {commessa.righe.map((r, i) => (
            <RigaArticolo
              key={r.riga_id ?? `${r.codice}-${i}`}
              caricoId={caricoId}
              riga={r}
              modificabile={modificabile}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

/** Il badge della disponibilità: la conferma che viene dal magazzino.
 *
 * Tre casi in ordine di gravità — manca qualcosa, è in produzione, è a
 * magazzino — perché è l'ordine in cui interessano a chi deve caricare. */
export function Disponibilita({ riga }: { riga: RigaComposizione }) {
  const d = riga.disponibilita;
  if (!d) {
    // Il motore di evadibilità non valuta tutte le righe: il **materiale di
    // consumo** (i pallet EPAL, gli imballi) resta fuori per scelta. «n/d» faceva
    // sembrare un dato mancante quello che è un dato che non esiste — e sono
    // proprio le righe che in M1 facevano risultare inevadibili 45 commesse.
    return (
      <span
        className="text-[10.5px] text-muted-foreground"
        title="Riga non valutata dal motore (es. materiale di consumo: pallet, imballi)"
      >
        non valutata
      </span>
    );
  }

  const data = d.pronta_dal ? new Date(d.pronta_dal).toLocaleDateString("it-IT") : null;
  // Il motore classifica già la riga: «01 - EVADIBILE IN RITARDO» vuol dire che
  // la data del piano è **oltre** la consegna chiesta. Va detto, perché una data
  // di piano già passata («in produzione dal 28/07» letto il 27/08) sembra una
  // buona notizia e invece è un ritardo — in locale il piano MES è fermo al 29
  // aprile, quindi succede su molte righe.
  const inRitardo = d.stato.includes("RITARDO") || (!!d.pronta_dal && d.pronta_dal < new Date().toISOString().slice(0, 10));
  if (d.manca > 0) {
    return (
      <span
        className="rounded bg-red-100 px-1.5 py-0.5 text-[10.5px] font-medium text-red-800"
        title={d.causale}
      >
        mancano {Math.round(d.manca)}
      </span>
    );
  }
  if (d.produzione > 0 && d.magazzino <= 0) {
    return (
      <span
        className={cn(
          "rounded px-1.5 py-0.5 text-[10.5px] font-medium",
          inRitardo ? "bg-orange-100 text-orange-900" : "bg-amber-100 text-amber-800",
        )}
        title={d.causale}
      >
        {inRitardo
          ? `in ritardo${data ? ` · era prevista il ${data}` : ""}`
          : `in produzione${data ? ` · dal ${data}` : ""}`}
      </span>
    );
  }
  if (d.produzione > 0) {
    return (
      <span
        className="rounded bg-amber-50 px-1.5 py-0.5 text-[10.5px] font-medium text-amber-800"
        title={d.causale}
      >
        {Math.round(d.magazzino)} a magazzino · resto in produzione
      </span>
    );
  }
  return (
    <span
      className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10.5px] font-medium text-emerald-800"
      title={d.causale}
    >
      a magazzino
    </span>
  );
}

function RigaArticolo({
  caricoId,
  riga,
  modificabile,
}: {
  caricoId: string;
  riga: RigaComposizione;
  modificabile: boolean;
}) {
  const scelte = useCpScelteComposizione(caricoId);
  const [bozza, setBozza] = useState<string>(riga.colli != null ? String(riga.colli) : "");
  // Il valore può cambiare **dal server** e non solo digitando: «tutte»/«nessuna»
  // riscrivono tutte le righe, e il ricalcolo può ridurre una quantità se la
  // commessa è calata. Senza questa sincronizzazione il campo restava sul numero
  // vecchio — cioè mostrava una quantità che sul carico non c'era più.
  useEffect(() => {
    setBozza(riga.colli != null ? String(riga.colli) : "");
  }, [riga.colli]);
  const senzaId = !riga.riga_id;
  const parziale =
    riga.colli != null && riga.colli_totali != null && riga.colli < riga.colli_totali;

  function salvaQuantita() {
    if (!riga.riga_id) return;
    const valore = Number(bozza);
    if (!Number.isFinite(valore) || valore === riga.colli) return;
    scelte.mutate([{ riga_id: riga.riga_id, colli: valore }]);
  }

  return (
    <li
      className={cn(
        "flex items-center gap-2 border-b px-2 py-1.5 text-xs last:border-0",
        riga.escluso && "bg-muted/40 text-muted-foreground line-through decoration-1",
      )}
    >
      {modificabile && !senzaId && (
        <input
          type="checkbox"
          checked={!riga.escluso}
          disabled={scelte.isPending}
          onChange={(e) =>
            scelte.mutate([{ riga_id: riga.riga_id as string, escluso: !e.target.checked }])
          }
          title={riga.escluso ? "Rimetti la riga sul carico" : "Togli la riga dal carico"}
        />
      )}
      <span className="min-w-0 flex-1 truncate" title={riga.descrizione ?? riga.codice ?? ""}>
        <span className="font-medium">{riga.codice}</span>
        {riga.descrizione && (
          <span className="ml-1.5 text-muted-foreground">{riga.descrizione}</span>
        )}
      </span>

      {/* La quantità: scrivibile quando la riga è dentro. Ridurla è il secondo
          modo di escludere — «tre pallet dei nove» del foglio. */}
      {modificabile && !senzaId && !riga.escluso ? (
        <span className="flex shrink-0 items-center gap-0.5 tabular-nums">
          <input
            type="number"
            min="0"
            max={riga.colli_totali ?? undefined}
            value={bozza}
            onChange={(e) => setBozza(e.target.value)}
            onBlur={salvaQuantita}
            onKeyDown={(e) => e.key === "Enter" && salvaQuantita()}
            className={cn(
              "h-6 w-16 rounded border px-1 text-right text-xs",
              parziale && "border-amber-400 bg-amber-50",
            )}
          />
          <span className="text-[10.5px] text-muted-foreground">/{riga.colli_totali ?? "—"}</span>
        </span>
      ) : (
        <span className="shrink-0 tabular-nums">
          {riga.colli ?? "—"}
          {riga.colli_totali != null && riga.colli_totali !== riga.colli && (
            <span className="text-[10.5px] text-muted-foreground">/{riga.colli_totali}</span>
          )}
        </span>
      )}

      <span className="w-[9.5rem] shrink-0 text-right">
        <Disponibilita riga={riga} />
      </span>
    </li>
  );
}

export function SelettoreCommessa({
  caricoId,
  onFatto,
  onScegli,
  iniziale = "",
  segnaposto = "Commesse aperte del cliente, o cerca un numero…",
}: {
  caricoId: string;
  onFatto: () => void;
  /** Al posto dell'aggancio diretto: chi usa il selettore decide cosa fare
   *  della commessa scelta (il drawer: conferma e resoconto). */
  onScegli?: (c: CommessaCandidata) => void;
  iniziale?: string;
  segnaposto?: string;
}) {
  const [q, setQ] = useState(iniziale);
  const { data, isFetching } = useCpCommesseCandidate(caricoId, q.trim());
  const aggiungi = useCpAggiungiCommessa(caricoId);
  const items = data?.items ?? [];

  return (
    <div className="rounded-md border p-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={segnaposto}
          className="h-8 w-full rounded border bg-card pl-7 pr-7 text-xs focus:outline-none focus:ring-2 focus:ring-ring"
        />
        {isFetching && (
          <Loader2 className="absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-muted-foreground" />
        )}
      </div>
      <ul className="mt-1 max-h-52 overflow-y-auto">
        {items.length === 0 ? (
          <li className="flex items-center gap-1.5 px-1 py-2 text-[11px] text-muted-foreground">
            <Package className="h-3.5 w-3.5" />
            {q.trim()
              ? `Nessuna commessa per «${q.trim()}».`
              : "Nessuna commessa aperta trovata per questo carico — se il cliente non è " +
                "riconosciuto in anagrafica non c'è un perimetro da proporre. Cerca per numero."}
          </li>
        ) : (
          items.map((c) => (
            <li key={c.ordine_numero}>
              <button
                type="button"
                disabled={c.gia_agganciata || aggiungi.isPending}
                onClick={() =>
                  onScegli ? onScegli(c) : aggiungi.mutate(c.ordine_numero, { onSuccess: onFatto })
                }
                className="flex w-full items-center justify-between gap-2 rounded px-1.5 py-1.5 text-left text-xs hover:bg-muted disabled:opacity-40"
              >
                <span className="min-w-0">
                  <span className="font-semibold tabular-nums">{c.ordine_numero}</span>
                  {c.riferimento_cliente && (
                    <span className="ml-1.5 text-[10.5px] text-muted-foreground">
                      «{c.riferimento_cliente}»
                    </span>
                  )}
                  <span className="ml-1.5 text-muted-foreground">{c.righe} righe</span>
                  {c.data_consegna && (
                    <span className="ml-1.5 text-[10.5px] text-muted-foreground">
                      consegna {new Date(c.data_consegna).toLocaleDateString("it-IT")}
                    </span>
                  )}
                  {/* Senza questo avviso la stessa merce finirebbe su due camion. */}
                  {c.su_altro_carico && (
                    <span className="ml-1.5 rounded bg-red-100 px-1 py-0.5 text-[9.5px] font-medium text-red-800">
                      già su un altro carico ({c.su_altro_carico})
                    </span>
                  )}
                </span>
                <span className="shrink-0 text-[10.5px] text-muted-foreground">
                  {c.gia_agganciata ? "già sul carico" : `residua ${Math.round(c.qty_residua)}`}
                </span>
              </button>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}


/**
 * A quali commesse è imputato questo carico, e **da dove lo sappiamo**.
 *
 * Non è la composizione: quella dice *cosa* parte e la NDP la congela, ed è
 * giusto. Questa dice *di chi è*, ed è la frase che il Cruscotto Commesse mostra
 * a chi guarda M1 — «questa merce è già impegnata», «questa commessa è già
 * uscita il 15 settembre». Per questo si corregge anche a NDP emessa: dire a
 * quale commessa appartiene la merce non è rifare il carico.
 *
 * Il caso che rende il gesto necessario: su 113 carichi con una nota di
 * prelievo, **7** hanno in fascicolo una *packing list* archiviata al posto
 * della nota. Lì il parser si astiene per costruzione, e senza questo pannello
 * chi conosce quel carico non potrebbe farci niente — che è il modo più sicuro
 * di far dire «non funziona» a tutto il resto della pagina.
 */
function Attribuzione({ caricoId }: { caricoId: string }) {
  const { data } = useCaricoPrevisto(caricoId);
  const correggi = useCpCorreggiAttribuzione(caricoId);
  const [apri, setApri] = useState(false);
  const [testo, setTesto] = useState("");
  const [errore, setErrore] = useState<string | null>(null);

  if (!data) return null;
  const vale = data.attribuzione_vale;
  const commesse = vale === "lettura" ? (data.commesse ?? []) : (data.commesse_documento ?? []);

  const salva = () => {
    setErrore(null);
    const numeri = [...new Set(testo.split(/[^0-9]+/).filter(Boolean).map(Number))];
    correggi.mutate(
      { commesse: numeri },
      {
        onSuccess: () => setApri(false),
        onError: (e: unknown) => setErrore(e instanceof Error ? e.message : String(e)),
      },
    );
  };

  return (
    <div className="rounded border bg-muted/30 px-2.5 py-2 text-[11.5px]">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium">
            Imputato a{" "}
            {commesse.length ? (
              <span className="font-mono">{commesse.join(", ")}</span>
            ) : (
              <span className="text-muted-foreground">nessuna commessa</span>
            )}
          </p>
          <p className="mt-0.5 text-muted-foreground">
            {vale === "manuale"
              ? "confermato a mano dalla Logistica"
              : vale === "documento"
                ? "letto dalla nota di prelievo, colonna «ordine»"
                : "⚠️ ricavato da un'email: non confermato da un documento"}
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setTesto(commesse.join(", "));
            setApri((v) => !v);
          }}
          className="shrink-0 rounded border px-2 py-1 text-[11px] font-medium hover:bg-muted"
        >
          Correggi
        </button>
      </div>

      {apri && (
        <div className="mt-2 space-y-1.5 border-t pt-2">
          <label className="block text-[11px] text-muted-foreground" htmlFor="attr">
            I numeri di commessa di questo carico, separati da virgola
          </label>
          <input
            id="attr"
            value={testo}
            onChange={(e) => setTesto(e.target.value)}
            className="h-8 w-full rounded border bg-card px-2 text-[12px] focus:outline-none focus:ring-2 focus:ring-ring"
            placeholder="es. 121, 341, 458"
          />
          {errore && <p className="text-[11px] text-red-700 dark:text-red-400">{errore}</p>}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setApri(false)}
              className="rounded px-2 py-1 text-[11px] hover:bg-muted"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={salva}
              disabled={correggi.isPending}
              className="rounded bg-foreground px-2 py-1 text-[11px] font-medium text-background disabled:opacity-50"
            >
              {correggi.isPending ? "Salvo…" : "Salva"}
            </button>
          </div>
          <p className="text-[10.5px] text-muted-foreground">
            Quello che scrivi qui vince su tutto e non viene più sovrascritto: né dal
            documento, né da una rilettura, né da Aida.
          </p>
        </div>
      )}
    </div>
  );
}
